import { useEffect, useState, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MapPin, Check, Camera, Utensils, Coffee, Building2, Palmtree, Cross, Wheat, Shirt, ChefHat, ShoppingCart, Cloud, Factory, Crown } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { CATEGORIES } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { useSiteImages } from "../hooks/use-site-images";

const ICONS = {
  restaurant: Utensils, cafe: Coffee, hotel: Building2, resort: Palmtree,
  villa_palace: Crown,
  hospital: Cross, bakery: Wheat, laundry: Shirt, catering: ChefHat,
  supermarket: ShoppingCart, cloud_kitchen: Cloud, central_kitchen: Factory,
};

const HERO_IMAGES = {
  restaurant: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1600&q=80",
  cafe: "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1600&q=80",
  hotel: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1600&q=80",
  resort: "https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?auto=format&fit=crop&w=1600&q=80",
  villa_palace: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80",
  hospital: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1600&q=80",
  bakery: "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&w=1600&q=80",
  laundry: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=1600&q=80",
  catering: "https://images.unsplash.com/photo-1555244162-803834f70033?auto=format&fit=crop&w=1600&q=80",
  supermarket: "https://images.unsplash.com/photo-1542838132-92c53300491e?auto=format&fit=crop&w=1600&q=80",
  cloud_kitchen: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1600&q=80",
  central_kitchen: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1600&q=80",
};

export default function SectorDetail() {
  const { slug } = useParams();
  const { t, lang, isRTL } = useLang();
  const siteImage = useSiteImages();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get(`/projects?category=${slug}`).then((r) => setProjects(r.data || []));
  }, [slug]);

  if (!CATEGORIES.includes(slug)) return <Navigate to="/sectors" replace />;

  const Icon = ICONS[slug] || Utensils;
  const s = t.sectorDetail[slug] || t.sectorDetail.restaurant;
  const heroImg = siteImage(`sector_${slug}`, HERO_IMAGES[slug] || HERO_IMAGES.restaurant);

  return (
    <div data-testid={`sector-detail-${slug}`}>
      {/* Hero with sector-specific image */}
      <section className="relative h-[520px] flex items-end overflow-hidden">
        <img src={heroImg} alt={t.categories[slug]} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a] via-[#0f172a]/60 to-[#0f172a]/20" />
        <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-14 text-white">
          <Link to="/sectors" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-xs mb-4 uppercase tracking-widest">
            <ArrowLeft className={`w-3.5 h-3.5 ${isRTL ? "rotate-180" : ""}`} /> {lang === "ar" ? "كل القطاعات" : "All sectors"}
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#ef4444] flex items-center justify-center shadow-xl">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div className="text-[10px] tracking-[0.35em] text-red-200 font-semibold uppercase">
              {lang === "ar" ? "قطاع" : "SECTOR"} · {t.categories[slug]}
            </div>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl mb-3">
            {s.h1}
          </h1>
          <p className="text-white/80 text-base max-w-2xl">{s.subtitle}</p>
        </div>
      </section>

      {/* What we deliver */}
      <section className="py-16 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mb-10">
            <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
              {t.sectorDetail.deliversEyebrow}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] leading-tight">{t.sectorDetail.deliversTitle}</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {s.delivers.map((d, i) => (
              <div key={i} data-testid={`sd-deliver-${i}`} className="flex items-start gap-3 bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                  <Check className="w-4 h-4 text-[#ef4444]" />
                </div>
                <span className="text-sm text-slate-700 leading-relaxed">{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mini case study */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm grid lg:grid-cols-2">
            <div className="relative min-h-[320px] bg-slate-200">
              <img src={heroImg} alt="Case study" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-5 start-5 inline-block bg-white/95 backdrop-blur rounded-lg px-3 py-1.5 text-[10px] tracking-widest text-[#ef4444] uppercase font-semibold">
                {t.sectorDetail.caseEyebrow}
              </div>
            </div>
            <div className="p-8 lg:p-12">
              <h3 className="font-display text-2xl text-[#0891b2] mb-2">{s.caseTitle}</h3>
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-6">{s.caseSub}</div>
              <div className="space-y-4 mb-6">
                {s.caseHighlights.map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="font-display text-xl text-[#ef4444] leading-none">{String(i + 1).padStart(2, "0")}</div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">{h.title}</div>
                      <div className="text-slate-600 text-sm">{h.text}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-slate-100">
                {s.caseStats.map((st, i) => (
                  <div key={i}>
                    <div className="font-display text-2xl text-[#0891b2]">{st.value}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">{st.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related projects */}
      {projects.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="inline-block text-[10px] tracking-[0.3em] text-[#ef4444] font-semibold mb-3 uppercase">
                {t.sectorDetail.projectsEyebrow}
              </div>
              <h2 className="font-display text-3xl text-[#0891b2]">{t.sectorDetail.projectsTitle}</h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {projects.slice(0, 6).map((p, i) => {
                const photoCount = (p.gallery?.length || 0) + (p.image ? 1 : 0);
                return (
                  <Link
                    key={p.id}
                    to={`/projects/${p.slug}`}
                    data-testid={`sd-project-${i}`}
                    className="group border border-slate-200 rounded-xl overflow-hidden hover:border-[#0891b2] hover:shadow-lg transition-all"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-slate-100 relative">
                      <img src={p.image} alt={p.title_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      {photoCount > 1 && (
                        <div className="absolute bottom-2 end-2 bg-[#0f172a]/85 text-white text-[10px] px-2 py-0.5 rounded flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          {photoCount} {t.projectExtras.photos}
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-display text-sm text-slate-900 mb-1 line-clamp-1">{lang === "ar" ? p.title_ar : p.title_en}</h4>
                      <div className="flex items-center gap-1 text-[11px] text-slate-500">
                        <MapPin className="w-3 h-3" />
                        {t.emirates[p.emirate] || (lang === "ar" ? p.location_ar : p.location_en)}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-50 via-white to-red-50/30">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl sm:text-4xl text-[#0891b2] mb-4">{s.ctaTitle}</h2>
          <p className="text-slate-600 mb-8">{s.ctaSubtitle}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/quote">
              <Button data-testid="sd-cta-quote" className="bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 px-6 font-semibold">
                {t.sectorDetail.ctaQuote} <Arrow className="w-4 h-4 ms-2" />
              </Button>
            </Link>
            <Link to="/consultation">
              <Button variant="outline" data-testid="sd-cta-cons" className="border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12 px-6 font-semibold">
                {t.sectorDetail.ctaBook}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
